import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Row, Col, Alert, Spin, Tag, Typography, Progress, Statistic } from 'antd';
import {
  PlayCircleOutlined,
  StopOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

// R2-AUTH: /api/v1/agents/* now requires a Bearer token — attach it the same
// way the authed admin clients (platformApi.ts / connectorApi.ts) do.
const agentAuthHeaders = (extra: Record<string, string> = {}): Record<string, string> => {
  const token =
    localStorage.getItem('authToken') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('auth_token');
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
};

interface AgentStatus {
  id: string;
  name: string;
  status: 'ready' | 'busy' | 'error' | 'offline';
  capabilities: string[];
  metrics: {
    tasks_completed: number;
    success_rate: number;
    avg_response_time: number;
  };
}

interface CoordinatorStatus {
  running: boolean;
  agents_count: number;
  tasks_completed: number;
  tasks_pending: number;
  uptime: number;
}

const AgentDashboard: React.FC = () => {
  const [coordinatorStatus, setCoordinatorStatus] = useState<CoordinatorStatus | null>(null);
  const [agents, setAgents] = useState<AgentStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchCoordinatorStatus = async () => {
    try {
      const response = await fetch('/api/v1/agents/coordinator/status', { headers: agentAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCoordinatorStatus(data);
        setError(null); // Clear any previous errors
      } else if (response.status === 503) {
        throw new Error('Backend service temporarily unavailable. Please wait and try again.');
      } else {
        throw new Error(`Failed to fetch coordinator status (${response.status})`);
      }
    } catch (err) {
      console.error('Error fetching coordinator status:', err);
      if (err instanceof Error && err.message.includes('fetch')) {
        setError('Unable to connect to backend services. Please check if all services are running.');
      } else {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await fetch('/api/v1/agents/agents', { headers: agentAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        // Convert object of agents to array
        const agentsArray = Object.values(data).map((agent: any) => ({
          id: agent.agent_id,
          name: agent.name,
          status: agent.status,
          capabilities: agent.capabilities,
          metrics: {
            tasks_completed: agent.metrics.tasks_completed,
            success_rate: agent.metrics.success_rate,
            avg_response_time: agent.metrics.average_response_time
          }
        }));
        setAgents(agentsArray);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchCoordinatorStatus(), fetchAgents()]);
    } finally {
      setLoading(false);
    }
  };

  const startCoordinator = async () => {
    try {
      const response = await fetch('/api/v1/agents/coordinator/start', {
        method: 'POST',
        headers: agentAuthHeaders()
      });
      if (response.ok) {
        await refreshData();
      } else {
        throw new Error('Failed to start coordinator');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start coordinator');
    }
  };

  const stopCoordinator = async () => {
    try {
      const response = await fetch('/api/v1/agents/coordinator/stop', {
        method: 'POST',
        headers: agentAuthHeaders()
      });
      if (response.ok) {
        await refreshData();
      } else {
        throw new Error('Failed to stop coordinator');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop coordinator');
    }
  };

  const testClassification = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/agents/demo/classify', {
        method: 'POST',
        headers: agentAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          document: 'This is a test document containing sensitive financial information and classified material.'
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Classification result:', result);
        await refreshData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Classification test failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(refreshData, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready': return 'green';
      case 'busy': return 'orange';
      case 'error': return 'red';
      case 'offline': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready': return <CheckCircleOutlined />;
      case 'busy': return <ClockCircleOutlined />;
      case 'error': return <ExclamationCircleOutlined />;
      case 'offline': return <StopOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Title level={2}>
          <ThunderboltOutlined className="mr-2" />
          Agent System Dashboard
        </Title>
        <Paragraph>
          Monitor and manage autonomous agents for document processing tasks.
        </Paragraph>
      </div>

      {/* Error Display */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      {/* Controls */}
      <Card title="Coordinator Controls" className="mb-6">
        <Space wrap>
          <Button
            type={coordinatorStatus?.running ? "default" : "primary"}
            icon={<PlayCircleOutlined />}
            onClick={startCoordinator}
            disabled={coordinatorStatus?.running || loading}
          >
            Start Coordinator
          </Button>

          <Button
            type="default"
            icon={<StopOutlined />}
            onClick={stopCoordinator}
            disabled={!coordinatorStatus?.running || loading}
          >
            Stop Coordinator
          </Button>

          <Button
            icon={<ReloadOutlined />}
            onClick={refreshData}
            loading={loading}
          >
            Refresh
          </Button>

          <Button
            type="dashed"
            onClick={testClassification}
            disabled={!coordinatorStatus?.running || loading}
          >
            Test Classification
          </Button>
        </Space>
      </Card>

      {/* Coordinator Status */}
      <Card title="Coordinator Status" className="mb-6">
        {coordinatorStatus ? (
          <Row gutter={24}>
            <Col span={6}>
              <Statistic
                title="Status"
                value={coordinatorStatus.running ? "Running" : "Stopped"}
                valueStyle={{ color: coordinatorStatus.running ? '#3f8600' : '#cf1322' }}
                prefix={coordinatorStatus.running ? <CheckCircleOutlined /> : <StopOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic title="Active Agents" value={coordinatorStatus.agents_count} />
            </Col>
            <Col span={6}>
              <Statistic title="Tasks Completed" value={coordinatorStatus.tasks_completed} />
            </Col>
            <Col span={6}>
              <Statistic title="Tasks Pending" value={coordinatorStatus.tasks_pending} />
            </Col>
          </Row>
        ) : (
          <div className="text-center py-8">
            <Spin size="large" />
            <div className="mt-2">Loading coordinator status...</div>
          </div>
        )}
      </Card>

      {/* Agents List */}
      <Card title={`Active Agents (${agents.length})`}>
        {agents.length > 0 ? (
          <Row gutter={[16, 16]}>
            {agents.map((agent) => (
              <Col key={agent.id} xs={24} sm={12} md={8} lg={6}>
                <Card size="small" hoverable>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div className="flex justify-between items-center">
                      <Text strong>{agent.name}</Text>
                      <Tag color={getStatusColor(agent.status)} icon={getStatusIcon(agent.status)}>
                        {agent.status}
                      </Tag>
                    </div>

                    <div>
                      <Text type="secondary">Capabilities:</Text>
                      <div className="mt-1">
                        {agent.capabilities.map((cap) => (
                          <Tag key={cap}>{cap}</Tag>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Text type="secondary">Tasks: </Text>
                      <Text>{agent.metrics.tasks_completed}</Text>
                      <br />
                      <Text type="secondary">Success Rate: </Text>
                      <Text>{(agent.metrics.success_rate * 100).toFixed(1)}%</Text>
                      <br />
                      <Text type="secondary">Avg Response: </Text>
                      <Text>{agent.metrics.avg_response_time.toFixed(2)}s</Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {coordinatorStatus?.running ? (
              <div>
                <ThunderboltOutlined className="text-4xl mb-2" />
                <div>No agents currently registered</div>
                <div className="text-sm">Agents will appear here when they connect to the coordinator</div>
              </div>
            ) : (
              <div>
                <StopOutlined className="text-4xl mb-2" />
                <div>Coordinator is stopped</div>
                <div className="text-sm">Start the coordinator to see active agents</div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AgentDashboard;